import { useEffect, useRef, useState } from "react";

/**
 * AnimatedNumber — smoothly interpolates between value changes so the digits
 * visibly roll over instead of snapping. Used on bot row P&L and trade counts
 * so the page feels constantly alive when values update.
 *
 * Props:
 *  - value: number  (the target value)
 *  - duration: ms   (how long to interpolate; default 1200)
 *  - decimals: int  (fixed decimals; default 0)
 *  - prefix / suffix: strings prepended/appended
 *  - className: tailwind classes
 *  - testId: data-testid
 */
export function AnimatedNumber({
  value,
  duration = 1200,
  decimals = 0,
  prefix = "",
  suffix = "",
  className = "",
  testId,
}) {
  const [display, setDisplay] = useState(value || 0);
  const fromRef = useRef(value || 0);
  const startRef = useRef(0);
  const rafRef = useRef(0);

  useEffect(() => {
    const target = Number(value) || 0;
    if (target === display) return undefined;
    fromRef.current = display;
    startRef.current = performance.now();

    const tick = (now) => {
      const elapsed = now - startRef.current;
      const t = Math.min(1, elapsed / duration);
      // Ease-out cubic so the roll feels natural
      const eased = 1 - Math.pow(1 - t, 3);
      const next = fromRef.current + (target - fromRef.current) * eased;
      setDisplay(next);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        setDisplay(target);
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, duration]);

  const formatted =
    decimals > 0
      ? Number(display).toFixed(decimals)
      : Math.round(Number(display)).toLocaleString();

  return (
    <span className={className} data-testid={testId}>
      {prefix}
      {formatted}
      {suffix}
    </span>
  );
}
